import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { BookingProgress } from "@/components/booking/BookingProgress";
import { BookingSummary } from "@/components/booking/BookingSummary";
import { BookingDetailsStep } from "@/components/booking/steps/BookingDetailsStep";
import { GrabStep } from "@/components/booking/steps/GrabStep";
import { ManVanStep } from "@/components/booking/steps/ManVanStep";
import { PaymentStep } from "@/components/booking/steps/PaymentStep";
import { ReviewStep } from "@/components/booking/steps/ReviewStep";
import { ServiceStep } from "@/components/booking/steps/ServiceStep";
import { SkipSizeStep } from "@/components/booking/steps/SkipSizeStep";
import { SupplementalItemsStep } from "@/components/booking/steps/SupplementalItemsStep";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getPrices } from "@/lib/mockApi";
import { clearBookingState, loadSavedBookingState, saveBookingState } from "@/lib/storage";
import { defaultBookingState, type BookingState } from "@/types/booking";

const steps = ["Service", "Size", "Extras", "Details", "Review", "Pay"];
const paymentStepIndex = steps.length - 1;
const historyStepStateKey = "wastekingBookingStep";

function hasCompletedPaidBooking(state: BookingState) {
  return state.paymentStatus === "succeeded" && Boolean(state.bookingReference.trim());
}

function normalizeLockedBookingState(state: BookingState) {
  if (!hasCompletedPaidBooking(state)) {
    return { ...state, currentStep: Math.min(state.currentStep, paymentStepIndex) };
  }

  return { ...state, currentStep: paymentStepIndex };
}

function getStepFromHistoryState(value: unknown) {
  if (!value || typeof value !== "object" || !(historyStepStateKey in value)) {
    return null;
  }

  const step = Number((value as Record<string, unknown>)[historyStepStateKey]);

  return Number.isInteger(step) ? Math.min(Math.max(step, 0), paymentStepIndex) : null;
}

function createStepHistoryState(step: number) {
  const currentHistoryState = window.history.state;
  const baseState = currentHistoryState && typeof currentHistoryState === "object" ? currentHistoryState : {};

  return { ...baseState, [historyStepStateKey]: step };
}

export function BookingWizard() {
  const isApplyingHistoryNavigationRef = useRef(false);
  const hasInitialisedHistoryRef = useRef(false);
  const historyStepDepthRef = useRef(0);
  const nextHistoryModeRef = useRef<"push" | "replace">("push");
  const [savedState, setSavedState] = useState<BookingState | null>(() => {
    const storedState = loadSavedBookingState();

    if (!storedState) {
      return null;
    }

    return normalizeLockedBookingState(storedState);
  });
  const [state, setState] = useState<BookingState>(defaultBookingState);
  const [maxStep, setMaxStep] = useState(state.currentStep);
  const [bookingSessionId, setBookingSessionId] = useState(0);
  const [isResumeDialogOpen, setIsResumeDialogOpen] = useState(
    Boolean(savedState?.postcode || savedState?.bookingDetails.postcode),
  );

  const shouldPrice = Boolean(state.postcode && state.service && (state.skipSize || state.loadSize || state.materialType || state.currentStep >= 2));
  const pricing = useQuery({
    queryKey: ["pricing", state.postcode, state.service, state.skipSize, state.loadSize, state.materialType, state.supplementalItems, state.discountResult],
    queryFn: () => getPrices(state.postcode, state.service, { skipSize: state.skipSize, loadSize: state.loadSize, materialType: state.materialType }, state),
    enabled: shouldPrice,
  });

  useEffect(() => {
    if (isResumeDialogOpen && savedState) {
      return;
    }

    saveBookingState(state);
  }, [isResumeDialogOpen, savedState, state]);

  useEffect(() => {
    if (pricing.data) {
      setState((current) => {
        if (hasCompletedPaidBooking(current)) {
          return current;
        }

        return { ...current, pricingResult: pricing.data ?? current.pricingResult };
      });
    }
  }, [pricing.data]);

  useEffect(() => {
    function handlePopState(event: PopStateEvent) {
      const historyStep = getStepFromHistoryState(event.state);

      if (historyStep === null) {
        return;
      }

      isApplyingHistoryNavigationRef.current = true;
      historyStepDepthRef.current = Math.max(historyStepDepthRef.current - 1, 0);
      setState((current) => normalizeLockedBookingState({ ...current, currentStep: historyStep }));
      setMaxStep((current) => Math.max(current, historyStep));
    }

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    if (isResumeDialogOpen) {
      return;
    }

    if (!hasInitialisedHistoryRef.current) {
      window.history.replaceState(createStepHistoryState(state.currentStep), "", window.location.href);
      hasInitialisedHistoryRef.current = true;
      return;
    }

    if (isApplyingHistoryNavigationRef.current) {
      isApplyingHistoryNavigationRef.current = false;
      return;
    }

    if (getStepFromHistoryState(window.history.state) !== state.currentStep) {
      if (nextHistoryModeRef.current === "replace") {
        window.history.replaceState(createStepHistoryState(state.currentStep), "", window.location.href);
      } else {
        window.history.pushState(createStepHistoryState(state.currentStep), "", window.location.href);
        historyStepDepthRef.current += 1;
      }

      nextHistoryModeRef.current = "push";
    }
  }, [isResumeDialogOpen, state.currentStep]);

  function updateState(patch: Partial<BookingState>) {
    setState((current) => {
      if (hasCompletedPaidBooking(current)) {
        return current;
      }

      return normalizeLockedBookingState({ ...current, ...patch });
    });
  }

  function continueSavedBooking() {
    if (!savedState) return;

    const nextState = normalizeLockedBookingState(savedState);
    setState(nextState);
    setMaxStep(nextState.currentStep);
    setBookingSessionId((current) => current + 1);
    setIsResumeDialogOpen(false);
    setSavedState(null);
  }

  function startNewBooking() {
    clearBookingState();
    setState(defaultBookingState);
    setMaxStep(defaultBookingState.currentStep);
    setBookingSessionId((current) => current + 1);
    setIsResumeDialogOpen(false);
    setSavedState(null);
  }

  function goToStep(step: number) {
    const nextStep = Math.min(Math.max(step, 0), maxStep);
    setState((current) => ({ ...current, currentStep: nextStep }));
  }

  function goNext() {
    setState((current) => {
      if (hasCompletedPaidBooking(current)) {
        return { ...current, currentStep: paymentStepIndex };
      }

      const nextStep = Math.min(current.currentStep + 1, steps.length - 1);
      setMaxStep((value) => Math.max(value, nextStep));
      return { ...current, currentStep: nextStep };
    });
  }

  function goBack() {
    if (hasCompletedPaidBooking(state)) {
      setState((current) => ({ ...current, currentStep: paymentStepIndex }));
      return;
    }

    if (getStepFromHistoryState(window.history.state) === state.currentStep && historyStepDepthRef.current > 0) {
      window.history.back();
      return;
    }

    nextHistoryModeRef.current = "replace";
    setState((current) => ({ ...current, currentStep: Math.max(current.currentStep - 1, 0) }));
  }

  const isBookingLocked = hasCompletedPaidBooking(state);
  const stepProps = { state, isLocked: isBookingLocked, updateState, goNext, goBack, goToStep, startNewBooking };
  const currentStep = renderStep(stepProps);
  const savedPostcode = savedState?.postcode || savedState?.bookingDetails.postcode;

  return (
    <div className="space-y-5 pb-28 sm:pb-0">
      <Dialog open={isResumeDialogOpen} onOpenChange={setIsResumeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Continue previous order?</DialogTitle>
            <DialogDescription>
              It looks like you were doing a previous order for {savedPostcode} postcode. Would you like to continue
              with that?
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={startNewBooking}>
              Start a new order
            </Button>
            <Button type="button" onClick={continueSavedBooking}>
              Continue order
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
        <div className="space-y-5">
          <BookingProgress steps={steps} currentStep={state.currentStep} maxStep={maxStep} onStepClick={goToStep} />
          <div className="lg:hidden">
            <BookingSummary state={state} isLocked={isBookingLocked} updateState={updateState} />
          </div>
          <div key={bookingSessionId}>{currentStep}</div>
        </div>
        <div className="hidden lg:block">
          <BookingSummary state={state} isLocked={isBookingLocked} updateState={updateState} />
        </div>
      </div>
    </div>
  );
}

function renderStep(stepProps: Parameters<typeof ServiceStep>[0]) {
  const { state } = stepProps;
  if (state.currentStep === 0) return <ServiceStep {...stepProps} />;
  if (state.currentStep === 1 && state.service === "skip") return <SkipSizeStep {...stepProps} />;
  if (state.currentStep === 1 && state.service === "man-van") return <ManVanStep {...stepProps} />;
  if (state.currentStep === 1 && state.service === "grab") return <GrabStep {...stepProps} />;
  if (state.currentStep === 2) return <SupplementalItemsStep {...stepProps} />;
  if (state.currentStep === 3) return <BookingDetailsStep {...stepProps} />;
  if (state.currentStep === 4) return <ReviewStep {...stepProps} />;
  if (state.currentStep === 5) return <PaymentStep {...stepProps} />;
  return <ServiceStep {...stepProps} />;
}
